import type { Article } from "./types";

export const article: Article = {
  slug: "brick-alternative",
  title: "Bible Shield vs Brick: a $59 object or a prayer",
  h1: "Bible Shield vs Brick",
  description:
    "Brick puts a physical object between you and your apps. Bible Shield puts a prayer there. Two very different answers to the same problem, compared honestly.",
  targets: [
    "brick app alternative",
    "bible shield vs brick",
    "brick phone blocker christian alternative",
  ],
  published: "2026-09-03",
  standfirst:
    "Brick is the most interesting product in this category because its answer is not software at all. Whether that suits you depends on where you are when the urge arrives.",
  takeaways: [
    "Brick is a physical device you tap your phone against to start a block, and tap again to end it. Their site prices it at $59.00 and lists iOS 17.0+ and Android 12.0+.",
    'The friction is spatial: to unblock, you have to physically go back to wherever you left the Brick. Their site says there are "no overrides or workarounds".',
    "Bible Shield's friction is not spatial. The phone can unlock anywhere, and the cost is a short prayer with a verse rather than a walk.",
    "Brick is the stronger design if you can reliably leave it in another room. It is the weaker one if the problem is at two in the morning, in bed, with the Brick on the nightstand.",
  ],
  body: [
    {
      type: "note",
      text: "Disclosure: we build Bible Shield. Everything below about Brick comes from Brick's own website, checked on 3 September 2026, and products change — check theirs before deciding.",
    },
    {
      type: "p",
      text: "Almost every product in this category is software arguing with software. Brick is not, and that makes it worth taking seriously even if you end up choosing something else.",
    },
    {
      type: "h2",
      id: "what-brick-is",
      text: "What Brick is",
    },
    {
      type: "p",
      text: 'A small physical square you tap your phone against. Their site describes the flow in three steps: choose the apps in their app, tap the Brick to start a session, and those apps stay blocked until you come back and tap it again. It is described as "durable, magnetic, and slip-resistant".',
    },
    {
      type: "p",
      text: "It costs $59.00 on their site, requires the hardware — the app alone is not the product — and supports iPhones on iOS 17.0 or later and Android devices on version 12.0 or later. One Brick can be used with several phones.",
    },
    {
      type: "h2",
      id: "friction-with-a-location",
      text: "Friction that has a location",
    },
    {
      type: "p",
      text: "This is Brick's genuinely good idea. Every software block can be undone wherever you are standing, because the undo lives on the same device as the temptation. Brick moves the undo somewhere else — the kitchen, the hallway, a desk drawer — so the cost of unblocking is measured in metres.",
    },
    {
      type: "p",
      text: "That is the same principle as charging your phone in another room, which is the single most effective change most people make about {the last hour of the day}, and Brick has productised it.",
      links: {
        "the last hour of the day": "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "h2",
      id: "where-it-breaks-down",
      text: "Where each one breaks down",
    },
    {
      type: "p",
      text: "Brick's weakness is the same as its strength. Spatial friction only works if the object is somewhere else, and the moment it is on the nightstand it costs about four seconds. Anybody who has moved a phone charger to another room and then carried the phone in anyway knows how that goes.",
    },
    {
      type: "p",
      text: "Bible Shield's weakness is that the unlock is always available. There is no walk. What stands between you and the app is a prayer, and if you do not want to pray it, you can read past it. That is a deliberate choice — a block a Christian cannot open is a block that gets deleted — but it is a weaker wall by any physical measure.",
    },
    {
      type: "note",
      text: "The honest version: Brick is better at making you stop. Bible Shield is better at making the stopping worth something. Those are different jobs and it is worth knowing which one you are failing at.",
    },
    {
      type: "h2",
      id: "the-hour-still-has-to-be-for-something",
      text: "The hour still has to be for something",
    },
    {
      type: "p",
      text: "This is the part neither product solves and it is the part that decides whether either survives. An evening reclaimed from a feed is not automatically a good evening; the first week of any of this is mostly boredom, and boredom is precisely what the feed was solving.",
    },
    {
      type: "p",
      text: "Brick is honest about being a wall — it does one thing and does it physically. Bible Shield's whole design is an argument that the wall is the easy half, and that the minute behind it is the point. Neither claim is provable in advance, and the useful test is not which sounds better but which one you are still using in March.",
    },
    {
      type: "p",
      text: "There is also a practical difference in cost shape. Brick is a one-off purchase of an object you can lose; Bible Shield is free to download and to pray with daily, with the paid tier optional. Neither of those is an argument on its own, but they are different kinds of commitment and it is worth noticing which one you find easier to abandon.",
    },
    {
      type: "h2",
      id: "when-to-pick-which",
      text: "When to pick which",
    },
    {
      type: "ul",
      items: [
        "Pick Brick if you can genuinely put it somewhere inconvenient, if you are on Android, or if you have tried software blockers and dismissed all of them.",
        "Pick Bible Shield if the problem arrives in bed, in a queue or in the car — places a physical device cannot be left behind from — and if you want the interruption to be a prayer rather than a walk.",
        "They are compatible. Brick blocks by session, Bible Shield by schedule and prayer, and neither prevents the other.",
      ],
    },
    {
      type: "p",
      text: "One caution about either: a new physical habit and a new spiritual one on the same weekend usually produces neither. Start one, and give it {a fortnight before adding the second}.",
      links: {
        "a fortnight before adding the second":
          "/prayers/prayer-for-a-new-start",
      },
    },
  ],
  faq: [
    {
      q: "How much does Brick cost?",
      a: "Their site lists the device at $59.00 as of 3 September 2026, and the hardware is required — the app alone is not the product. One Brick can be used with several phones.",
    },
    {
      q: "Is there a Christian alternative to Brick?",
      a: "Bible Shield is the closest on iPhone: the same idea of putting something between you and the app, except the something is a short prayer with a verse rather than a physical device you have to walk back to.",
    },
    {
      q: "Which is harder to bypass, Brick or an app blocker?",
      a: "Brick, when the device is genuinely in another room — that is its whole design. When it is on the nightstand the advantage disappears, and the comparison becomes about what the block does rather than how strong it is.",
    },
    {
      q: "Can I use Brick and Bible Shield together?",
      a: "Yes, they operate differently and neither prevents the other. Add them a fortnight apart rather than together; two new systems on the same day usually ends with neither surviving.",
    },
  ],
  related: ["opal-alternative", "screen-time-limits-dont-work"],
};
